import {
  GAClientIdManager,
  MemoryStorage,
  CookieStorage,
  LocalStorage,
  GoogleAnalyticsClient,
} from '@grammarly/gnarclientweb'

import { isEligibleForTracking } from '../../lib/tracking'
import { trackAccountCreation, trackEncodedUserId } from '../../lib/tracking/google-tag-manager/events'
import { Cookie, setCookie } from '../../lib/utils/cookies'
import { delay } from '../../lib/utils/promise'
import { day, week } from '../../lib/utils/time'
import { logDataLayerPushError } from '../../lib/tracking/google-tag-manager/utils'
import { LogErrorMetrics } from '../../lib/tracking/felog'
import { encodeSha256 } from '../../lib/utils/crypto'
import { getConfig } from '../../config'
import { User } from '../../lib/services/user'
import { noop } from '../../lib/utils/noop'

/** Extension Notification */
const setExtension = (msg: string) => {
  if (IS_CLIENT_SIDE) {
    window.GR_EXTENSION_SEND?.(msg)
  }
}

export const extensionChangedUser = () => {
  setExtension('changed-user')
}

const EXTENSION_WAIT_DELAY_MS = 20
export const extensionWaitUntilReady = async (maxWaitTimeMs = 500): Promise<boolean> => {
  if (IS_CLIENT_SIDE) {
    const startTime = Date.now()
    while (Date.now() - startTime <= maxWaitTimeMs) {
      if (window.GR_EXTENSION_SEND) {
        return true
      }
      await delay(EXTENSION_WAIT_DELAY_MS)
    }
  }

  return false
}

/** Google Analytics, GTM, Bing, Cookie Tracking */

const createGAClient = () => {
  if (!IS_CLIENT_SIDE) {
    return { ga: noop, setUserId: noop, sendPageview: noop } as unknown as GoogleAnalyticsClient
  }
  const { acquisitionTracking, domains } = getConfig()

  const client = new GoogleAnalyticsClient(
    acquisitionTracking.googleAnalytics.id,
    new GAClientIdManager(new CookieStorage(domains.cookie), [new LocalStorage(), new MemoryStorage()])
  )
  client.ga('require', 'displayfeatures')
  client.ga('require', 'ecommerce')
  return client
}

const GoogleAnalyticsTracker = createGAClient()

const setIsGrammarlyUserCookie = () => {
  setCookie(Cookie.isGrammarlyUser, true, week)
}

export const trackLoginSuccess = (user: User) => {
  if (isEligibleForTracking(user)) {
    GoogleAnalyticsTracker.sendPageview('/G:User_Login_Succeeded')
  } else {
    disableBingUETTracking()
  }
  setIsGrammarlyUserCookie()
}

// Set the cookie the disabled bing tracking
export const setDisableBingUETTrackingCookie = () => {
  setCookie(Cookie.disableBingUETTracking, '1', day)
}

// Disable Bing's pageShow/pageHide tracking.
// Note: this behavior is undocumented, it can break any time.
const disableBingPageVisibilityEvents = () => {
  // 1. Find a possible bing config on the window object (it's dynamic, but seems to always start with 'ueto')
  const bingTrackingSuspects = Object.keys(window).filter((key) => key.startsWith('ueto'))
  bingTrackingSuspects.forEach((suspect) => {
    // #ToDo improve Window typings to avoid any usage
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bingConfig = (window as any)[suspect]
    if (bingConfig && bingConfig.uetConfig) {
      // 2. Overwrite the related config variable.
      bingConfig.uetConfig.disableVisibilityEvents = true
    }
  })
}

const disableBingUETTracking = () => {
  setDisableBingUETTrackingCookie()
  disableBingPageVisibilityEvents()
}

export const trackAccountCreated = (user: User) => {
  setIsGrammarlyUserCookie()
  if (isEligibleForTracking(user)) {
    encodeSha256(user.id)
      .then((encodedUserId) => trackEncodedUserId(encodedUserId))
      .catch((e) => logDataLayerPushError(e, 'encodedUserId', LogErrorMetrics.EncodeUserIdGTMError))
      .finally(() => {
        trackAccountCreation()
      })
    GoogleAnalyticsTracker.sendPageview('/G:User_Account_Created')
  } else {
    disableBingUETTracking()
  }
}
